import { useMemo } from "react";

import { WIDGET_GRID_APPEARANCE } from "@/components/organisms/Table/Table.tools";
import { Tree } from "@/components/organisms/Tree";
import type { TreeLayoutType } from "@/components/organisms/Tree/index";
import { Values } from "@/constants";
import { Utils } from "@/utils";

/** An amount as a report shows it, with nothing shown as a dash. */
function formatSharedCurrency(
  value: string | number | null | undefined,
): string {
  if (value == null || value === "") return "—";
  return Utils.fCurrency(Number(value));
}

type SharedReportCell = {
  columnKey: string;
  amount?: string | number | null;
  formattedAmount?: string | null;
};

type SharedReportColumn = {
  key: string;
  label: string;
};

type SharedReportTreeNode = {
  rowKey: string;
  label: string;
  cells?: SharedReportCell[] | null;
  children?: SharedReportTreeNode[] | null;
};

type SharedTreeRow = {
  id: string;
  label: string;
  cellsByKey: Record<
    string,
    { amount?: string | number | null; formattedAmount?: string | null }
  >;
  children?: { id: string }[];
};

function indexTreeNodes(
  nodes: SharedReportTreeNode[],
  rowsById: Record<string, SharedTreeRow>,
): SharedTreeRow[] {
  return nodes.map((node) => {
    const cellsByKey: SharedTreeRow["cellsByKey"] = {};
    for (const cell of node.cells ?? []) {
      cellsByKey[cell.columnKey] = {
        amount: cell.amount,
        formattedAmount: cell.formattedAmount,
      };
    }

    const children = indexTreeNodes(node.children ?? [], rowsById);
    const row: SharedTreeRow = {
      id: node.rowKey,
      label: node.label,
      cellsByKey,
      children: children.map((child) => ({ id: child.id })),
    };
    rowsById[row.id] = row;
    return row;
  });
}

export function SharedReportTreeBody({
  roots,
  columns,
}: {
  roots: SharedReportTreeNode[];
  columns: SharedReportColumn[];
}) {
  const layout = useMemo<TreeLayoutType | null>(() => {
    if (!roots.length) return null;

    const rowsById: Record<string, SharedTreeRow> = {};
    const heads = indexTreeNodes(roots, rowsById);
    const valueColumns =
      columns.length > 0
        ? columns.filter((column) => column.key !== "name")
        : [{ key: "amount", label: "Amount" }];

    return {
      isSummaryRowVisible: false,
      isCellBordersHidden: false,
      isOpenDefaultDepth: 2,
      fetchingOptions: {
        row: {
          type: "LOCAL" as const,
          getData: (id: string) => rowsById[id] ?? null,
        },
        column: {
          type: "LOCAL" as const,
          getData: () => [
            {
              label: "Account",
              objectKey: "label",
              fieldType: "string" as const,
              headerFieldType: "string" as const,
              getValue: (branch: SharedTreeRow) => branch?.label,
              width: 200,
              minWidth: 160,
              isLockedLeft: true,
            },
            ...valueColumns.map((column) => ({
              label: column.label,
              objectKey: column.key,
              fieldType: "currency" as const,
              headerFieldType: "string" as const,
              getValue: (branch: SharedTreeRow) => {
                const cell = branch?.cellsByKey?.[column.key];
                if (cell?.formattedAmount) return cell.formattedAmount;
                return formatSharedCurrency(cell?.amount);
              },
              width: Values.DEFAULT_COLUMN_WIDTH,
              minWidth: 100,
            })),
          ],
        },
      },
      heads,
      getChildrenIds: (branch: SharedTreeRow) =>
        branch?.children?.map((child) => child.id) ?? [],
    };
  }, [columns, roots]);

  if (!layout) {
    return null;
  }

  return (
    <div className="flex-1 min-h-0 w-full flex flex-col overflow-hidden">
      <Tree layout={{ ...layout, appearance: WIDGET_GRID_APPEARANCE }} />
    </div>
  );
}
